import { useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Card, Nav, OverlayTrigger, Tooltip } from 'react-bootstrap';
import styles from './styles.module.css';
import cn from 'classnames';
import Message from '../Message';
import Email from '../Email';
import QRCode from '../QR/QRCode';
import Link from '../Link';
import { Image } from 'ui';
import { setResendSentAt } from 'store/common/slices/general';
import { selectResendSentAt } from 'store/common/selectors/general';
import { IS_SANDBOX_ENV } from 'utils/constants';

const TABS = [
    { key: 'qr code', disabled: false },
    { key: 'link', disabled: false },
    { key: 'sms', disabled: IS_SANDBOX_ENV },
    { key: 'email', disabled: false },
];

const SANDBOX_DISABLED_MESSAGE = 'This feature is not available in the sandbox environment.';

const VerificationView = ({
    verificationOption,
    isReauthenticate,
    sessionTheme,
    link,
    handleBackClick,
    ...props
}) => {
    const [selected, setSelected] = useState('qr code');
    const dispatch = useDispatch();

    // Held in the persisted `general` slice (not local state) so each channel's
    // resend cool-down survives switching tabs — which unmounts these components —
    // and leaving and re-entering the device-links page within the tab session.
    const sentAt = useSelector(selectResendSentAt);
    const markSent = (channel) => () => dispatch(setResendSentAt({ channel, time: Date.now() }));

    const COMPONENTS = {
        'qr code': <QRCode link={link} {...props} verificationOption={verificationOption} />,
        link: (
            <Link
                link={link}
                {...props}
                verificationOption={verificationOption}
                theme={sessionTheme}
            />
        ),
        sms: (
            <Message
                {...props}
                verificationOption={verificationOption}
                theme={sessionTheme}
                isReauthenticate={isReauthenticate}
                sentAt={sentAt.sms}
                onSent={markSent('sms')}
            />
        ),
        email: (
            <Email
                {...props}
                verificationOption={verificationOption}
                theme={sessionTheme}
                isReauthenticate={isReauthenticate}
                sentAt={sentAt.email}
                onSent={markSent('email')}
            />
        ),
    };

    return (
        <>
            <Card.Header className={styles.cardHeader}>
                <Alert.Link className={styles.backArrow} onClick={handleBackClick}>
                    <Image name="Back.svg" />
                </Alert.Link>
                <p className={styles.cardTitle}>Continue With Your Verification</p>
            </Card.Header>
            <Card.Body className={styles.cardBody}>
                <Nav
                    fill
                    className={styles.navWrapper}
                    variant="tabs"
                    defaultActiveKey={selected}
                    onSelect={(selectedKey) => setSelected(selectedKey)}
                >
                    {TABS.map(({ key, disabled }) => {
                        const navLink = (
                            <Nav.Link
                                className={cn(
                                    styles.navlink,
                                    selected === key && styles.active,
                                    disabled && styles.disabled
                                )}
                                eventKey={key}
                                active={selected === key}
                                disabled={disabled}
                            >
                                {key.toUpperCase()}
                            </Nav.Link>
                        );

                        return (
                            <Nav.Item key={key}>
                                {disabled ? (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id={`tooltip-${key}`}>
                                                {SANDBOX_DISABLED_MESSAGE}
                                            </Tooltip>
                                        }
                                    >
                                        <span className={styles.disabledTabWrapper}>{navLink}</span>
                                    </OverlayTrigger>
                                ) : (
                                    navLink
                                )}
                            </Nav.Item>
                        );
                    })}
                </Nav>
                {COMPONENTS[selected]}
            </Card.Body>
        </>
    );
};

VerificationView.propTypes = {
    verificationOption: PropTypes.string,
    isReauthenticate: PropTypes.bool,
    sessionTheme: PropTypes.string,
    link: PropTypes.string,
    handleBackClick: PropTypes.func,
};

export default VerificationView;
